import React from 'react';
import { useTranslation } from 'react-i18next';

interface Step {
  id: number;
  label: string;
  description?: string;
}

interface WorkflowStepperProps {
  steps: Step[];
  currentStep: number;
  completedSteps: number[];
  namespace?: string; // Translation namespace for step labels
}

// Mapping from translation key prefixes to actual namespace files
// Some keys use different prefixes than the namespace file name
const prefixToNamespace: Record<string, string> = {
  'dataProcessing': 'dataProcessing',
  'dataUpload': 'dataUpload',
  'dataAdjustments': 'dataAdjustments',
  'adjustmentsOfData': 'dataAdjustments', // Key prefix differs from namespace
  'dataCloud': 'dataCloud',
  'training': 'training',
  'common': 'common',
};

// Helper to extract namespace from translation key (e.g., 'adjustmentsOfData.workflow.steps.upload' → 'dataAdjustments')
const getNamespaceFromKey = (key: string): string | undefined => {
  const prefix = key.split('.')[0];
  return prefixToNamespace[prefix];
};

const WorkflowStepper: React.FC<WorkflowStepperProps> = ({
  steps,
  currentStep,
  completedSteps,
  namespace
}) => {
  // Uses multiple namespaces since step labels come from different features
  const { t } = useTranslation(['common', 'dataUpload', 'dataProcessing', 'dataAdjustments', 'dataCloud', 'training']);

  // Translate with automatic namespace detection from key prefix, or use provided namespace
  const translateKey = (key: string): string => {
    // If namespace prop is provided, use it directly
    if (namespace) {
      return t(key, { ns: namespace });
    }
    // Otherwise try to detect from key prefix (legacy support)
    const ns = getNamespaceFromKey(key);
    return ns ? t(key, { ns }) : t(key);
  };
  const getStepStatus = (stepId: number) => {
    if (completedSteps.includes(stepId)) return 'completed';
    if (stepId === currentStep) return 'current';
    if (stepId < currentStep) return 'completed';
    return 'upcoming';
  };

  return (
    <div className="w-full px-2 sm:px-4 py-4 sm:py-6 mb-4 sm:mb-8 overflow-x-auto">
      <div className="flex items-start justify-between min-w-max sm:min-w-0" role="list">
        {steps.map((step, index) => {
          const status = getStepStatus(step.id);
          const isLast = index === steps.length - 1;

          return (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center flex-shrink-0" role="listitem">
                {/* Step Circle */}
                <div className="relative">
                  <div
                    className={`
                      w-8 h-8 sm:w-10 md:w-12 sm:h-10 md:h-12 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold
                      transition-all duration-300 transform
                      ${status === 'completed'
                        ? 'bg-green-500 text-white sm:scale-110'
                        : status === 'current'
                        ? 'bg-primary text-primary-foreground ring-2 sm:ring-4 ring-primary/20 sm:scale-110'
                        : 'bg-muted text-muted-foreground'
                      }
                    `}
                  >
                    {status === 'completed' ? (
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      step.id
                    )}
                  </div>
                  
                  {/* Pulse effect for current step */}
                  {status === 'current' && (
                    <div className="absolute inset-0 rounded-full bg-primary animate-ping opacity-25 hidden sm:block"></div>
                  )}
                </div>

                {/* Step Label */}
                <div className="mt-2 sm:mt-3 text-center">
                  <p className={`
                    text-[10px] sm:text-xs md:text-sm font-medium transition-colors duration-300 max-w-[60px] sm:max-w-[80px] md:max-w-none
                    ${status === 'current' ? 'text-primary' : status === 'completed' ? 'text-green-600' : 'text-muted-foreground'}
                  `}>
                    {translateKey(step.label)}
                  </p>
                  {step.description && (
                    <p className="hidden md:block text-xs text-muted-foreground mt-1 max-w-[120px]">
                      {translateKey(step.description)}
                    </p>
                  )}
                </div>
              </div>

              {/* Connector Line (decorative — not part of step list semantics) */}
              {!isLast && (
                <div
                  className="flex-1 mx-1 sm:mx-2 md:mx-4 min-w-[20px] sm:min-w-[40px] mt-4 sm:mt-5 md:mt-6"
                  role="presentation"
                  aria-hidden="true"
                >
                  <div className="relative h-0.5 sm:h-1 bg-muted rounded">
                    <div
                      className={`
                        absolute h-full rounded transition-all duration-500
                        ${completedSteps.includes(step.id + 1) || status === 'completed'
                          ? 'bg-green-500 w-full'
                          : status === 'current'
                          ? 'bg-primary w-1/2'
                          : 'bg-muted w-0'
                        }
                      `}
                    />
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default WorkflowStepper;